import React, { useEffect, useRef, useState } from "react";
import { ArrowDown, Loader2 } from "lucide-react";

// Pull-to-refresh for the INSTALLED iPhone app (Levi 2026-10-15, thread
// #1610-1: "on Iphone, people can't swipe downwards to refresh the page, but
// on android they can").
//
// WHY THIS EXISTS: Android Chrome's installed app keeps the browser's native
// pull-to-refresh; iOS does not — WebKit gives a home-screen web app launched
// from `apple-mobile-web-app-capable` (which this portal sets, and which
// InstallAppNudge.jsx tells every iPhone employee to use) NO pull-to-refresh
// gesture at all. There is no CSS switch that turns it back on and no native
// API to call, so the gesture has to be implemented in the app.
//
// SCOPE — iOS standalone ONLY (`navigator.standalone === true`, an iOS-only
// property). Everywhere else the platform already has the gesture and ours
// would fire a second reload on top of it: Android Chrome (browser AND
// installed app) and iOS Safari-in-a-tab both keep theirs. The portal's own CSS
// does not suppress native pull-to-refresh on either engine — measured
// overscroll-behavior-y: auto and overflow-y: visible on html and body in both
// WebKit and Chromium.
//
// A refresh is a real page reload — the same thing Android's native gesture
// does — so every page's data, and the app's freshness clock, reset together.
const THRESHOLD = 70; // finger travel that arms the refresh
const MAX_PULL = 96; // furthest the indicator travels
const RESISTANCE = 0.5; // finger travel -> indicator travel

/** iOS home-screen app only. `navigator.standalone` exists on iOS alone. */
export function isInstalledIosApp(win = typeof window !== "undefined" ? window : undefined) {
  return win?.navigator?.standalone === true;
}

/** True while the caller is inside a drawer/dialog — never hijack those. */
function insideOverlay(el) {
  for (let n = el; n && n !== document.body; n = n.parentElement) {
    if (n.matches?.('[role="dialog"],[role="alertdialog"],[data-vaul-drawer],[data-vaul-drawer-wrapper],[data-vaul-overlay]')) {
      return true;
    }
  }
  return false;
}

/** True if any ancestor is scrolled away from its top — the drag belongs to it. */
function insideScrolledAncestor(el) {
  for (let n = el; n && n !== document.body; n = n.parentElement) {
    if (n.scrollTop > 0) return true;
  }
  return false;
}

export default function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const armed = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const active = useRef(false);
  const pulling = useRef(false);

  useEffect(() => {
    if (!isInstalledIosApp()) return undefined; // platform already has it

    const reset = () => {
      active.current = false;
      pulling.current = false;
      armed.current = false;
      setPull(0);
    };

    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return reset();
      // Only from the very top of the page, and never from inside a drawer,
      // a dialog, or a container the user has already scrolled.
      if (window.scrollY > 0) return;
      if (insideOverlay(e.target) || insideScrolledAncestor(e.target)) return;
      startX.current = e.touches[0].clientX;
      startY.current = e.touches[0].clientY;
      active.current = true;
      armed.current = false;
      pulling.current = false;
    };

    const onTouchMove = (e) => {
      if (!active.current || e.touches.length !== 1) return;
      const dy = e.touches[0].clientY - startY.current;
      const dx = e.touches[0].clientX - startX.current;
      // Upward, sideways, or the page scrolled under us: hand it back.
      if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || window.scrollY > 0) {
        return reset();
      }
      pulling.current = true;
      armed.current = dy * RESISTANCE >= THRESHOLD;
      setPull(Math.min(dy * RESISTANCE, MAX_PULL));
    };

    const onTouchEnd = () => {
      if (!active.current || !pulling.current) return reset();
      if (!armed.current) return reset();
      active.current = false;
      pulling.current = false;
      setPull(MAX_PULL);
      setRefreshing(true);
      // The reload is the refresh — same end state as Android's native gesture.
      window.location.reload();
    };

    // Every listener stays PASSIVE on purpose. A non-passive touchmove on
    // document makes the browser wait on JS for every scroll frame in the whole
    // app, and nothing here needs to cancel anything: the gesture only arms at
    // scrollY 0, where there is no scroll left to take, and iOS's own
    // rubber-band during the pull is the same motion Android's native gesture
    // shows. The pull is measured, not wrestled.
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd);
    document.addEventListener("touchcancel", reset);
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", reset);
    };
  }, []);

  if (!isInstalledIosApp()) return null;
  if (pull === 0 && !refreshing) return null;

  const released = pull >= THRESHOLD;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center pt-[env(safe-area-inset-top)]"
      style={{ transform: `translateY(${pull}px)` }}
      aria-hidden="true"
      data-testid="pull-to-refresh"
    >
      <div className="mt-3 flex items-center gap-2 rounded-full border border-white/10 bg-zinc-900/95 px-3 py-1.5 shadow-lg backdrop-blur">
        {refreshing ? (
          <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
        ) : (
          <ArrowDown
            className="h-4 w-4 text-zinc-400 transition-transform"
            style={{ transform: `rotate(${released ? 180 : 0}deg)` }}
          />
        )}
        <span className="text-xs font-medium text-zinc-300">
          {refreshing ? "Refreshing…" : released ? "Release to refresh" : "Pull to refresh"}
        </span>
      </div>
    </div>
  );
}
